import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Visibility as ObservatoryIcon,
  Business as BusinessIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  WbSunny as WeatherIcon,
  CardMembership as SubscriptionIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { hasObservatoryAdminCapabilities } from '../../utils/userCapabilities';
import { DEFAULT_VIEW_OPTIONS, getAllowedDefaultViews } from '../../constants/defaultViews';
import ExternalStorageCard from '../../components/ExternalStorageCard';
import ExternalStorageDialog from '../../components/ExternalStorageDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import ApiKeysSection from '../../components/ApiKeysSection';
import ObservatoryCustomIcon from '../../components/icons/ObservatoryIcon';

const Profile = () => {
  const { user, setUser, updateUserPreferences } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(true);
  const [defaultObservatoryId, setDefaultObservatoryId] = useState(user?.preferences?.default_observatory_id || '');
  const [defaultView, setDefaultView] = useState(user?.preferences?.default_view || 'classic_user');
  const [weatherProvider, setWeatherProvider] = useState(user?.preferences?.weather_forecast_provider || '');
  const [weatherApiKey, setWeatherApiKey] = useState(user?.preferences?.weather_forecast_api_key || '');
  const [weatherSaving, setWeatherSaving] = useState(false);

  // Sync preference-based state when user object updates (e.g. after login)
  useEffect(() => {
    if (user?.preferences) {
      setDefaultView(user.preferences.default_view || 'classic_user');
      setWeatherProvider(user.preferences.weather_forecast_provider || '');
      setWeatherApiKey(user.preferences.weather_forecast_api_key || '');
      setDefaultObservatoryId(user.preferences.default_observatory_id || '');
      setCalibrationEnabled(user.preferences.calibration_enabled ?? '');
      setRotationMatchFlats(user.preferences.require_rotation_matched_flats || '');
    }
  }, [user?.preferences]);
  const [allObservatories, setAllObservatories] = useState([]);
  const [calibrationEnabled, setCalibrationEnabled] = useState(user?.preferences?.calibration_enabled ?? '');
  const [rotationMatchFlats, setRotationMatchFlats] = useState(user?.preferences?.require_rotation_matched_flats || '');

  // External storage state (Issue #413)
  const [storageConfigs, setStorageConfigs] = useState([]);
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState(null);
  const [testingConfigId, setTestingConfigId] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // Organization memberships state
  const [orgMemberships, setOrgMemberships] = useState([]);
  const [orgMembershipsLoading, setOrgMembershipsLoading] = useState(true);

  // Resolved subscription tier (own, inherited from org/observatory, or default)
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // Avatar state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_enabled: false,
    pushover_enabled: false,
    pushover_user_key: '',
    pushover_devices: '',
    system_events: false,
    observation_events: true
  });
  const [editingPushover, setEditingPushover] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [testingNotifications, setTestingNotifications] = useState(false);

  // Edit form state
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: ''
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [passwordError, setPasswordError] = useState(null);

  // Password visibility state
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Load user memberships (observatories)
  useEffect(() => {
    const fetchMemberships = async () => {
      const userId = user?._id || user?.id;
      console.log('[Profile] Fetching memberships for user:', userId, 'Full user:', user);

      if (!userId) {
        console.log('[Profile] No user ID, skipping membership fetch');
        setMembershipsLoading(false);
        return;
      }
      try {
        setMembershipsLoading(true);
        const response = await apiService.getUserMemberships(userId);
        console.log('[Profile] Memberships response:', response);
        if (response?.success) {
          const membershipData = response.data || [];
          console.log('[Profile] Setting memberships state to:', membershipData);
          setMemberships(membershipData);
        } else {
          console.error('[Profile] Memberships API returned error:', response);
        }
      } catch (err) {
        console.error('[Profile] Failed to load memberships:', err);
      } finally {
        setMembershipsLoading(false);
      }
    };
    fetchMemberships();
  }, [user]);

  // Load all observatories for admin/server_admin users (who may not have memberships)
  useEffect(() => {
    const isAdmin = user?.role === 'admin' || user?.role === 'server_admin';
    if (isAdmin && !membershipsLoading && memberships.length === 0) {
      const fetchObservatories = async () => {
        try {
          const response = await apiService.getObservatories();
          if (response?.data?.observatories) {
            setAllObservatories(response.data.observatories);
          }
        } catch (err) {
          console.error('Failed to load observatories:', err);
        }
      };
      fetchObservatories();
    }
  }, [user?.role, membershipsLoading, memberships.length]);

  // Load external storage configs (Issue #413)
  useEffect(() => {
    const fetchStorageConfigs = async () => {
      try {
        const response = await apiService.getExternalStorageConfigs();
        if (response?.success) {
          setStorageConfigs(response.data?.configs || []);
        }
      } catch (err) {
        console.error('Failed to load external storage configs:', err);
      }
    };
    fetchStorageConfigs();
  }, []);

  // Load user organization memberships
  useEffect(() => {
    const fetchOrgMemberships = async () => {
      try {
        setOrgMembershipsLoading(true);
        const response = await apiService.getUserOrganizations();
        if (response?.data?.organizations) {
          setOrgMemberships(response.data.organizations);
        }
      } catch (err) {
        console.error('[Profile] Failed to load organization memberships:', err);
      } finally {
        setOrgMembershipsLoading(false);
      }
    };
    fetchOrgMemberships();
  }, []);

  // Load the caller's resolved subscription tier. /me returns { entitlements, subscription };
  // entitlements.source tells us whether the tier is the user's own, inherited from an
  // organization/observatory, or the free default.
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setSubscriptionLoading(true);
        const res = await apiService.getMySubscription();
        setSubscription(res?.data?.entitlements || null);
      } catch (err) {
        console.error('[Profile] Failed to load subscription:', err);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscription();
  }, []);

  const handleStorageSave = async (data, configId) => {
    setStorageLoading(true);
    setStorageError(null);
    try {
      if (configId) {
        await apiService.updateExternalStorageConfig(configId, data);
      } else {
        await apiService.createExternalStorageConfig(data);
      }
      // Refresh list
      const response = await apiService.getExternalStorageConfigs();
      if (response?.success) setStorageConfigs(response.data?.configs || []);
      setStorageDialogOpen(false);
      setEditingConfig(null);
      setSuccess(configId ? 'Storage configuration updated' : 'Storage configuration added');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to save storage configuration';
      setStorageError(msg);
    } finally {
      setStorageLoading(false);
    }
  };

  const handleStorageDelete = (config) => {
    setConfirm({
      title: 'Delete Storage Configuration',
      message: (
        <span>Delete storage configuration <strong>{config.display_name}</strong>?</span>
      ),
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          await apiService.deleteExternalStorageConfig(config._id);
          setStorageConfigs(prev => prev.filter(c => c._id !== config._id));
          setSuccess('Storage configuration deleted');
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(err.response?.data?.error?.message || 'Failed to delete storage configuration');
          throw err;
        }
      },
    });
  };

  const handleStorageTest = async (configId) => {
    setTestingConfigId(configId);
    try {
      const result = await apiService.testExternalStorageConfig(configId);
      if (result?.success) {
        setSuccess('Connection test successful');
      } else {
        setError(result?.error?.message || 'Connection test failed');
      }
      // Refresh to get updated test status
      const response = await apiService.getExternalStorageConfigs();
      if (response?.success) setStorageConfigs(response.data?.configs || []);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Connection test failed');
      setTimeout(() => setError(null), 5000);
    } finally {
      setTestingConfigId(null);
    }
  };

  // Initialize edit data when user changes
  useEffect(() => {
    if (user) {
      setEditData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        username: user.username || ''
      });
      const notifs = user.preferences?.notifications || {};
      const isAdmin = user.role === 'server_admin';
      setNotificationPrefs({
        email_enabled: notifs.email_enabled || false,
        pushover_enabled: notifs.pushover_enabled || false,
        pushover_user_key: notifs.pushover_user_key || '',
        pushover_devices: notifs.pushover_devices || '',
        system_events: isAdmin ? (notifs.system_events !== false) : false,
        observation_events: notifs.observation_events !== false
      });
    }
  }, [user]);

  const saveNotificationPrefs = async (updatedPrefs) => {
    setNotificationPrefs(updatedPrefs);
    setNotificationSaving(true);
    try {
      const response = await apiService.saveNotificationPreferences(updatedPrefs);
      if (response?.success) {
        setUser(prev => prev ? {
          ...prev,
          preferences: { ...prev.preferences, notifications: updatedPrefs }
        } : prev);
      } else {
        console.error('Failed to save notification preferences:', response);
        setError('Failed to save notification preferences');
      }
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
      setError('Failed to save notification preferences');
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setAvatarFile(null);
    setAvatarPreview(null);
    // Reset to original values
    setEditData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      username: user.username || ''
    });
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Avatar image must be less than 2MB');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    // If user has a saved avatar, delete it from server
    if (user?.avatar_url) {
      try {
        const response = await apiService.deleteAvatar();
        if (response?.success) {
          setUser(response.data);
        }
      } catch (err) {
        console.error('Failed to delete avatar:', err);
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let avatarWarning = null;

      // Upload avatar if a new file was selected
      if (avatarFile) {
        try {
          const avatarResponse = await apiService.uploadAvatar(avatarFile);
          if (avatarResponse?.success) {
            setUser(avatarResponse.data);
          } else {
            const avatarErr = avatarResponse?.error;
            avatarWarning = typeof avatarErr === 'string' ? avatarErr : avatarErr?.message || 'Failed to upload avatar';
          }
        } catch (avatarErr) {
          console.error('Avatar upload error:', avatarErr);
          avatarWarning = 'Avatar upload failed. Profile changes will still be saved.';
        }
      }

      const response = await apiService.updateProfile(editData);
      console.log('Profile update response:', response);

      if (response?.success) {
        setUser(response.data);
        setIsEditing(false);
        setAvatarFile(null);
        setAvatarPreview(null);
        if (avatarWarning) {
          setError(avatarWarning);
        } else {
          setSuccess('Profile updated successfully');
          setTimeout(() => setSuccess(null), 3000);
        }
      } else {
        const respErr = response?.error;
        setError(typeof respErr === 'string' ? respErr : respErr?.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      const catchErr = err.response?.data?.error;
      const errMsg = typeof catchErr === 'string' ? catchErr : catchErr?.message || err.message || 'Failed to update profile';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setPasswordError(null);

    try {
      const response = await apiService.updateProfile({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      if (response?.success) {
        setPasswordDialogOpen(false);
        setPasswordError(null);
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        // Reset password visibility state
        setShowPasswords({
          current: false,
          new: false,
          confirm: false
        });

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setPasswordError(response?.error || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'server_admin': return 'secondary';
      case 'admin': return 'error';
      case 'instructor': return 'warning';
      case 'student': return 'primary';
      case 'observer': return 'info';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'server_admin': return 'Server Admin';
      case 'admin': return 'Admin';
      case 'instructor': return 'Instructor';
      case 'student': return 'Student';
      case 'observer': return 'Observer';
      default: return 'User';
    }
  };

  const getCapabilityChips = (membership) => {
    const capabilities = [];
    
    // Check modern permissions structure first
    if (membership.permissions) {
      // Owner capability (special permission)
      if (membership.is_owner || membership.role === 'owner') {
        capabilities.push({ label: 'Owner', color: 'error', description: 'Transfer/delete observatory' });
      }
      
      // Check each permission individually
      if (membership.permissions.can_admin) {
        capabilities.push({ label: 'Admin', color: 'warning', description: 'Configure observatory, manage members' });
      }
      if (membership.permissions.can_operate) {
        capabilities.push({ label: 'Operate', color: 'info', description: 'Manage queue, modify any observation' });
      }
      if (membership.permissions.can_observe) {
        capabilities.push({ label: 'Observe', color: 'primary', description: 'Create observations, modify own observations' });
      }
      if (membership.permissions.can_view) {
        capabilities.push({ label: 'View', color: 'default', description: 'View observatory info, queue, own observations' });
      }
    } else {
      // Fall back to legacy role mapping
      switch (membership.role) {
        case 'owner':
          capabilities.push(
            { label: 'Owner', color: 'error', description: 'Transfer/delete observatory' },
            { label: 'Admin', color: 'warning', description: 'Configure observatory, manage members' },
            { label: 'Operate', color: 'info', description: 'Manage queue, modify any observation' },
            { label: 'Observe', color: 'primary', description: 'Create observations, modify own observations' },
            { label: 'View', color: 'default', description: 'View observatory info, queue, own observations' }
          );
          break;
        case 'admin':
          capabilities.push(
            { label: 'Admin', color: 'warning', description: 'Configure observatory, manage members' },
            { label: 'Operate', color: 'info', description: 'Manage queue, modify any observation' },
            { label: 'Observe', color: 'primary', description: 'Create observations, modify own observations' },
            { label: 'View', color: 'default', description: 'View observatory info, queue, own observations' }
          );
          break;
        case 'operator':
          capabilities.push(
            { label: 'Operate', color: 'info', description: 'Manage queue, modify any observation' },
            { label: 'Observe', color: 'primary', description: 'Create observations, modify own observations' },
            { label: 'View', color: 'default', description: 'View observatory info, queue, own observations' }
          );
          break;
        case 'observer':
          capabilities.push(
            { label: 'Observe', color: 'primary', description: 'Create observations, modify own observations' },
            { label: 'View', color: 'default', description: 'View observatory info, queue, own observations' }
          );
          break;
        case 'viewer':
        case 'member':
          capabilities.push(
            { label: 'View', color: 'default', description: 'View observatory info, queue, own observations' }
          );
          break;
        default:
          // Unknown role - grant basic view access
          capabilities.push(
            { label: 'View', color: 'default', description: 'View observatory info, queue, own observations' }
          );
          break;
      }
    }
    
    return capabilities;
  };

  // When the profile is opened in a floating window the host adds
  // ?standalone=1; tighten the visual scale so the editor fits without
  // forcing the user to scroll just to see headings.
  const isStandalone = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('standalone');
  const compactSx = isStandalone ? {
    '& .MuiTypography-h4': { fontSize: '1.25rem' },
    '& .MuiTypography-h5': { fontSize: '1.05rem' },
    '& .MuiTypography-h6': { fontSize: '0.95rem' },
    '& .MuiTypography-subtitle1': { fontSize: '0.9rem' },
    '& .MuiTypography-subtitle2': { fontSize: '0.8rem' },
    '& .MuiTypography-body1': { fontSize: '0.82rem' },
    '& .MuiTypography-body2': { fontSize: '0.75rem' },
    '& .MuiTypography-caption': { fontSize: '0.65rem' },
    '& .MuiInputBase-input': { fontSize: '0.82rem' },
    '& .MuiInputLabel-root': { fontSize: '0.82rem' },
    '& .MuiInputLabel-shrink': { fontSize: '0.75rem' },
    '& .MuiButton-root': { fontSize: '0.72rem' },
    '& .MuiChip-root': { fontSize: '0.7rem', height: 22 },
    '& .MuiAlert-root': { fontSize: '0.75rem', py: 0.25 },
    '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } },
    '& .MuiPaper-root': { p: 1.5 },
    // Force the page's two top-level Grid items (the 4/8 split on
    // route-navigated /profile) into an even 50/50 in standalone
    // mode so each card occupies exactly half the floating window.
    // Targeted via the md-4 / md-8 generated class names so nested
    // Grids inside the cards keep their own ratios.
    '& > .MuiGrid-container > .MuiGrid-grid-md-4': {
      flexBasis: '50%',
      maxWidth: '50%',
    },
    '& > .MuiGrid-container > .MuiGrid-grid-md-8': {
      flexBasis: '50%',
      maxWidth: '50%',
    },
  } : {};

  return (
    <Box sx={compactSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">
          User Profile
        </Typography>
        {error && (
          <Alert severity="error" sx={{ py: 0, flex: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ py: 0, flex: 1 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={avatarPreview || user?.avatar_url}
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                mb: 2,
                bgcolor: 'primary.main'
              }}
            >
              {!avatarPreview && !user?.avatar_url && <PersonIcon sx={{ fontSize: 60 }} />}
            </Avatar>
            <Typography variant="h5" gutterBottom>
              {user?.first_name} {user?.last_name}
            </Typography>

            {!isEditing && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  fullWidth
                >
                  Edit Profile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LockIcon />}
                  onClick={() => { setPasswordError(null); setPasswordDialogOpen(true); }}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Change Password
                </Button>
              </Box>
            )}
            
            {isEditing && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  fullWidth
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {!isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Full Name</Typography>
                  </Box>
                  <Typography variant="body1">{`${user?.first_name || ''} ${user?.last_name || ''}`}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                  </Box>
                  <Typography variant="body1">{user?.email || 'Not set'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Username</Typography>
                  </Box>
                  <Typography variant="body1">{user?.username || 'Not set'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
                    <SecurityIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">System Role</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={getRoleLabel(user?.role)}
                      color={getRoleColor(user?.role)}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {user?.role === 'server_admin' && 'Full server access with unrestricted abilities on all features.'}
                      {user?.role === 'admin' && 'Administrative access to manage users and observations'}
                      {user?.role === 'instructor' && 'Can create courses and manage student observations'}
                      {user?.role === 'student' && 'Can submit observations for instructor approval'}
                      {user?.role === 'observer' && 'Can view assigned observations and results'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
                    <SubscriptionIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Subscription</Typography>
                  </Box>
                  {subscriptionLoading ? (
                    <CircularProgress size={16} />
                  ) : subscription ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={subscription.display_name || subscription.plan_key}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                      {(subscription.source === 'organization' || subscription.source === 'observatory') && (
                        <Typography variant="body2" color="text.secondary">
                          Inherited from {subscription.source_name
                            || (subscription.source === 'organization' ? 'an organization' : 'an observatory')}
                        </Typography>
                      )}
                      {subscription.source === 'default' && (
                        <Typography variant="body2" color="text.secondary">Default tier</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body1">None</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 160, flexShrink: 0 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {orgMemberships.length > 1 ? 'Organizations' : 'Organization'}
                    </Typography>
                  </Box>
                  {orgMembershipsLoading ? (
                    <CircularProgress size={16} />
                  ) : orgMemberships.length === 0 ? (
                    <Typography variant="body1">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {orgMemberships.map((membership) => {
                        const org = membership.organization || {};
                        const isOwner = membership.is_owner;
                        return (
                          <Box key={org._id || org.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={isOwner ? 'Owner' : 'Member'}
                              size="small"
                              variant={isOwner ? 'filled' : 'outlined'}
                              color={isOwner ? 'error' : 'default'}
                              sx={{ width: 72 }}
                            />
                            <Typography variant="body1">{org.name || 'Unknown'}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box component="form" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    src={avatarPreview || user?.avatar_url}
                    sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                  >
                    {!avatarPreview && !user?.avatar_url && <PersonIcon sx={{ fontSize: 40 }} />}
                  </Avatar>
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoCameraIcon />}
                      size="small"
                      disabled={loading}
                    >
                      Upload Photo
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/gif"
                        onChange={handleAvatarChange}
                      />
                    </Button>
                    {(avatarPreview || user?.avatar_url) && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleAvatarRemove}
                        disabled={loading}
                        sx={{ ml: 1 }}
                      >
                        Remove
                      </Button>
                    )}
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      JPG, PNG, or GIF. Max 2MB.
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editData.first_name}
                      onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                      disabled={loading}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={editData.last_name}
                      onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                      disabled={loading}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                      disabled={loading}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={editData.username}
                      onChange={(e) => setEditData({...editData, username: e.target.value})}
                      disabled={loading}
                      required
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Statistics
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Account Created
                </Typography>
                <Typography variant="body1">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US') : 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Login
                </Typography>
                <Typography variant="body1">
                  {user?.last_login_at ? new Date(user.last_login_at).toLocaleDateString('en-US') : 'Never'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Email Verified
                </Typography>
                <Typography variant="body1">
                  {user?.email_verified ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Account Status
                </Typography>
                <Typography variant="body1" color="success.main">
                  Active
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Notification Preferences - moved here from right column */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon />
                Notification Preferences
              </Typography>
              {(notificationPrefs.email_enabled || notificationPrefs.pushover_enabled) && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={testingNotifications}
                  startIcon={testingNotifications ? <CircularProgress size={16} /> : undefined}
                  onClick={async () => {
                    setTestingNotifications(true);
                    setError(null);
                    try {
                      const response = await apiService.testNotifications({
                        email: notificationPrefs.email_enabled,
                        pushover: notificationPrefs.pushover_enabled
                      });
                      if (response?.success) {
                        setSuccess('Test notification sent successfully');
                        setTimeout(() => setSuccess(null), 5000);
                      } else {
                        setError(typeof response?.error === 'string' ? response.error : 'Failed to send test notification');
                      }
                    } catch (err) {
                      const errMsg = err.response?.data?.error;
                      setError(typeof errMsg === 'string' ? errMsg : errMsg?.message || 'Failed to send test notification');
                    } finally {
                      setTestingNotifications(false);
                    }
                  }}
                >
                  {testingNotifications ? 'Sending...' : 'Test'}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={notificationPrefs.email_enabled} onChange={(e) => saveNotificationPrefs({ ...notificationPrefs, email_enabled: e.target.checked })} />}
                label="Email Notifications"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2, mt: -0.5, fontSize: '0.8rem' }}>
                Receive at {user?.email || 'your email'}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={notificationPrefs.pushover_enabled} onChange={(e) => saveNotificationPrefs({ ...notificationPrefs, pushover_enabled: e.target.checked })} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Pushover
                    <Tooltip title="Learn more about Pushover">
                      <IconButton size="small" onClick={(e) => { e.preventDefault(); window.open('https://pushover.net/', '_blank', 'noopener'); }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
              {notificationPrefs.pushover_enabled && (
                <Box sx={{ ml: 4, mt: 1 }}>
                  {editingPushover ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField fullWidth label="Pushover User Key" value={notificationPrefs.pushover_user_key}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, pushover_user_key: e.target.value })}
                        placeholder="u1234abc5678def..." helperText="From pushover.net/dashboard" size="small" />
                      <TextField fullWidth label="Device Names" value={notificationPrefs.pushover_devices}
                        onChange={(e) => setNotificationPrefs({ ...notificationPrefs, pushover_devices: e.target.value })}
                        placeholder="iphone, pixel" helperText="Comma-separated. Blank = all devices." size="small" />
                      <Box>
                        <Button variant="contained" size="small" startIcon={<SaveIcon />} disabled={notificationSaving}
                          onClick={async () => { await saveNotificationPrefs(notificationPrefs); setEditingPushover(false); }}>Save</Button>
                        <Button size="small" sx={{ ml: 1 }} onClick={() => {
                          const notifs = user?.preferences?.notifications || {};
                          setNotificationPrefs(prev => ({ ...prev, pushover_user_key: notifs.pushover_user_key || '', pushover_devices: notifs.pushover_devices || '' }));
                          setEditingPushover(false);
                        }}>Cancel</Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Key: {notificationPrefs.pushover_user_key ? '•••' + notificationPrefs.pushover_user_key.slice(-3) : 'Not set'}</Typography>
                      <Typography variant="body2" color="text.secondary">Devices: {notificationPrefs.pushover_devices || 'All'}</Typography>
                      <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditingPushover(true)} sx={{ mt: 0.5, alignSelf: 'flex-start' }}>Edit</Button>
                    </Box>
                  )}
                </Box>
              )}
            </FormGroup>
            {(notificationPrefs.email_enabled || notificationPrefs.pushover_enabled) && user?.role === 'server_admin' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Notify me about:</Typography>
                <FormGroup row>
                  <FormControlLabel control={<Checkbox checked={notificationPrefs.system_events} onChange={(e) => saveNotificationPrefs({ ...notificationPrefs, system_events: e.target.checked })} />} label="System Events" />
                  <FormControlLabel control={<Checkbox checked={notificationPrefs.observation_events} onChange={(e) => saveNotificationPrefs({ ...notificationPrefs, observation_events: e.target.checked })} />} label="Observation Events" />
                </FormGroup>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {/* Default View Preference */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ObservatoryCustomIcon />
              Default View
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which view to show when you first log in.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Start View</InputLabel>
              <Select
                value={defaultView}
                onChange={(e) => {
                  const value = e.target.value;
                  setDefaultView(value);
                  updateUserPreferences({ default_view: value });
                  setSuccess('Default view updated');
                  setTimeout(() => setSuccess(null), 3000);
                }}
                label="Start View"
              >
                {(() => {
                  // Admin views are offered to admin-ish users only; everyone
                  // sees the two non-admin views. Same option list as the admin
                  // Edit-User dialog (constants/defaultViews.js).
                  const isServerAdmin = user?.role === 'server_admin';
                  const isAdminish = isServerAdmin || user?.role === 'admin' || user?.role === 'instructor' || hasObservatoryAdminCapabilities(memberships);
                  const allowed = getAllowedDefaultViews({ isServerAdmin: false, hasObservatoryAdmin: isAdminish });
                  // Service Center is server-admin only; surface it for true
                  // server admins in addition to the permissive list above.
                  const allowedViews = isServerAdmin ? [...allowed, 'service_center'] : allowed;
                  return DEFAULT_VIEW_OPTIONS.filter(o => allowedViews.includes(o.value)).map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ));
                })()}
              </Select>
              <FormHelperText>
                {DEFAULT_VIEW_OPTIONS.find(o => o.value === defaultView)?.helperText}
              </FormHelperText>
            </FormControl>
          </Paper>

          {/* Observatory Access Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ObservatoryIcon />
              Observatory Access
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {membershipsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : memberships.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {user?.role === 'server_admin' ? (
                  'As a Server Administrator you have owner, administer, and user access to all observatories.'
                ) : user?.role === 'admin' ? (
                  'You are not a member of any observatories. As an administrator, you have access to all observatories.'
                ) : (
                  'You are not a member of any observatories.'
                )}
              </Typography>
            ) : (
              <List dense>
                {memberships.map((membership) => (
                  <ListItem key={membership.observatory_id || membership._id}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {membership.observatory_name || membership.name || 'Unknown Observatory'}
                          </Typography>
                          {getCapabilityChips(membership).map((capability, index) => (
                            <Tooltip
                              key={index}
                              title={capability.description}
                              placement="top"
                            >
                              <Chip
                                label={capability.label}
                                color={capability.color}
                                size="small"
                                variant={capability.label === 'Owner' ? 'filled' : 'outlined'}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      }
                      secondary={membership.observatory_location || membership.location || null}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            {/* Default Observatory Selector */}
            {(() => {
              const isAdmin = user?.role === 'admin' || user?.role === 'server_admin';
              const hasAccess = memberships.length > 0 || isAdmin;
              if (!hasAccess || membershipsLoading) return null;

              // Build options from memberships, or from all observatories for admins without memberships
              const options = memberships.length > 0
                ? memberships.map(m => ({
                    id: m.observatory_id || m._id,
                    name: m.observatory_name || m.name || 'Unknown Observatory'
                  }))
                : allObservatories.map(o => ({
                    id: o._id,
                    name: o.name
                  }));

              if (options.length === 0) return null;

              return (
                <>
                  <Divider sx={{ my: 2 }} />
                  <FormControl fullWidth size="small">
                    <InputLabel>Default Observatory</InputLabel>
                    <Select
                      value={defaultObservatoryId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDefaultObservatoryId(value);
                        updateUserPreferences({ default_observatory_id: value || null });
                      }}
                      label="Default Observatory"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {options.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Pre-selected when submitting new observations</FormHelperText>
                  </FormControl>
                </>
              );
            })()}
          </Paper>

          {/* Calibration Preferences */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon />
              Calibration Defaults
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set your default calibration preferences. These will be used for new observations unless overridden at the observation level.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Calibration</InputLabel>
                  <Select
                    value={calibrationEnabled}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCalibrationEnabled(value);
                      updateUserPreferences({
                        calibration_enabled: value === '' ? null : value === 'true'
                      });
                    }}
                    label="Calibration"
                  >
                    <MenuItem value="">
                      <em>Inherit from Observatory</em>
                    </MenuItem>
                    <MenuItem value="true">Enabled</MenuItem>
                    <MenuItem value="false">Disabled</MenuItem>
                  </Select>
                  <FormHelperText>
                    Default calibration setting for your observations
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rotation-Matched Flats</InputLabel>
                  <Select
                    value={rotationMatchFlats}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRotationMatchFlats(value);
                      updateUserPreferences({
                        require_rotation_matched_flats: value || null
                      });
                    }}
                    label="Rotation-Matched Flats"
                  >
                    <MenuItem value="">
                      <em>Inherit from Observatory</em>
                    </MenuItem>
                    <MenuItem value="required">Required</MenuItem>
                    <MenuItem value="preferred">Preferred</MenuItem>
                    <MenuItem value="ignore">Ignore</MenuItem>
                  </Select>
                  <FormHelperText>
                    Whether flat frames must match the rotator angle
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* External Storage Section (Issue #413) */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUploadIcon />
                External Storage
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setEditingConfig(null); setStorageError(null); setStorageDialogOpen(true); }}
              >
                Add
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {storageConfigs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No external storage destinations configured. Add one to automatically copy FITS files to your cloud storage after observations complete.
              </Typography>
            ) : (
              storageConfigs.map(config => (
                <ExternalStorageCard
                  key={config._id}
                  config={config}
                  onEdit={(c) => { setEditingConfig(c); setStorageError(null); setStorageDialogOpen(true); }}
                  onDelete={handleStorageDelete}
                  onTest={handleStorageTest}
                  testing={testingConfigId === config._id}
                />
              ))
            )}
          </Paper>
          <ExternalStorageDialog
            open={storageDialogOpen}
            onClose={() => { setStorageDialogOpen(false); setEditingConfig(null); setStorageError(null); }}
            onSave={handleStorageSave}
            config={editingConfig}
            loading={storageLoading}
            error={storageError}
          />

          {/* API Keys (#1115): programmatic access without storing a password */}
          <ApiKeysSection />

          {/* Weather Forecast Source */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WeatherIcon />
              Weather Forecast Source
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure an external weather forecast provider to display forecast data alongside current conditions in the Operations Control view.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Forecast Provider</InputLabel>
              <Select
                value={weatherProvider}
                onChange={(e) => {
                  const value = e.target.value;
                  setWeatherProvider(value);
                  if (!value) {
                    setWeatherApiKey('');
                    updateUserPreferences({ weather_forecast_provider: null, weather_forecast_api_key: null });
                    setSuccess('Weather forecast source cleared');
                    setTimeout(() => setSuccess(null), 3000);
                  }
                }}
                label="Forecast Provider"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="openweathermap">OpenWeatherMap</MenuItem>
                <MenuItem value="visualcrossing">Visual Crossing</MenuItem>
                <MenuItem value="open_meteo">Open-Meteo (free, no key required)</MenuItem>
              </Select>
              <FormHelperText>
                {weatherProvider === 'openweathermap' && 'Requires a free or paid API key from openweathermap.org'}
                {weatherProvider === 'visualcrossing' && 'Requires a free or paid API key from visualcrossing.com'}
                {weatherProvider === 'open_meteo' && 'Free weather API, no API key needed'}
                {!weatherProvider && 'Select a provider to enable weather forecasting'}
              </FormHelperText>
            </FormControl>
            {weatherProvider && weatherProvider !== 'open_meteo' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="API Key"
                  type="password"
                  value={weatherApiKey}
                  onChange={(e) => setWeatherApiKey(e.target.value)}
                  placeholder={weatherProvider === 'openweathermap' ? 'e.g. a1b2c3d4e5f6...' : 'Enter API key'}
                  helperText={weatherProvider === 'openweathermap'
                    ? 'Get a free key at openweathermap.org/api'
                    : 'Get a key from your provider dashboard'}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={weatherSaving || !weatherApiKey}
                  startIcon={weatherSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={async () => {
                    setWeatherSaving(true);
                    try {
                      await updateUserPreferences({
                        weather_forecast_provider: weatherProvider,
                        weather_forecast_api_key: weatherApiKey,
                      });
                      setSuccess('Weather forecast settings saved');
                      setTimeout(() => setSuccess(null), 3000);
                    } catch (e) {
                      setError('Failed to save weather settings');
                    } finally {
                      setWeatherSaving(false);
                    }
                  }}
                  sx={{ mt: 0.5, minWidth: 80 }}
                >
                  Save
                </Button>
              </Box>
            )}
            {weatherProvider === 'open_meteo' && (
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  disabled={weatherSaving}
                  startIcon={weatherSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={async () => {
                    setWeatherSaving(true);
                    try {
                      await updateUserPreferences({
                        weather_forecast_provider: 'open_meteo',
                        weather_forecast_api_key: null,
                      });
                      setSuccess('Weather forecast settings saved');
                      setTimeout(() => setSuccess(null), 3000);
                    } catch (e) {
                      setError('Failed to save weather settings');
                    } finally {
                      setWeatherSaving(false);
                    }
                  }}
                  sx={{ mt: 1 }}
                >
                  Save
                </Button>
              </Box>
            )}
          </Paper>

        </Grid>
      </Grid>

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => { setPasswordError(null); setPasswordDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setPasswordError(null)}>
              {passwordError}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              type={showPasswords.current ? 'text' : 'password'}
              label="Current Password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
              disabled={loading}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle current password visibility"
                      onClick={() => togglePasswordVisibility('current')}
                      edge="end"
                    >
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showPasswords.new ? 'text' : 'password'}
              label="New Password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText="Must be at least 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => togglePasswordVisibility('new')}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showPasswords.confirm ? 'text' : 'password'}
              label="Confirm New Password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
              disabled={loading}
              error={passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password}
              helperText={
                passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password 
                  ? "Passwords don't match" 
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => togglePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPasswordError(null); setPasswordDialogOpen(false); }} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordChange} 
            variant="contained"
            disabled={loading || !passwordData.current_password || !passwordData.new_password || passwordData.new_password !== passwordData.confirm_password}
          >
            {loading ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
};

export default Profile;